/* eslint-disable @typescript-eslint/ban-ts-comment */
import { DateTime } from '@cheqroom/date-time';
import { DateTime as LuxonDateTime } from 'luxon';

export const isAMPM = (): boolean => window.global.central.hoursFormat().includes('a');

export class DateFormatter {
	// e.g. "2/16/2026"
	static shortDate(date: DateTime): string {
		return date.getLuxonDate().toLocaleString(LuxonDateTime.DATE_SHORT);
	}

	// e.g. "2/16/2026, 3:45 PM"
	static shortDateTime(date: DateTime): string {
		return date.getLuxonDate().toLocaleString(LuxonDateTime.DATETIME_SHORT);
	}

	// e.g. "Feb 16, 2026"
	static abbreviatedDate(date: DateTime): string {
		return date.getLuxonDate().toLocaleString(LuxonDateTime.DATE_MED);
	}

	// e.g. "Mon, Feb 16, 2026"
	static abbreviatedDateWithWeekDay(date: DateTime): string {
		return date.getLuxonDate().toLocaleString(LuxonDateTime.DATE_MED_WITH_WEEKDAY);
	}

	// e.g. "Feb 16, 2026, 3:45 PM" (12h) or "Feb 16, 2026, 15:45" (24h)
	static abbreviatedDateTime(
		date: DateTime,
		options?: {
			setZoneUTC?: boolean;
		}
	): string {
		return LuxonDateTime.fromJSDate(date.toDate(), {
			zone: options?.setZoneUTC ? 'utc' : undefined,
		}).toLocaleString({
			...LuxonDateTime.DATETIME_MED,
			hourCycle: isAMPM() ? 'h12' : 'h23',
		});
	}

	// e.g. "Mon, Feb 16, 2026, 3:45 PM" (12h) or "Mon, Feb 16, 2026, 15:45" (24h)
	static abbreviatedDateTimeWithWeekDay(
		date: DateTime,
		options?: {
			setZoneUTC?: boolean;
		}
	): string {
		return LuxonDateTime.fromJSDate(date.toDate(), {
			zone: options?.setZoneUTC ? 'utc' : undefined,
		}).toLocaleString({
			...LuxonDateTime.DATETIME_MED_WITH_WEEKDAY,
			hourCycle: isAMPM() ? 'h12' : 'h23',
		});
	}

	// e.g. "Thu, Feb 19 2:30AM" (12h) or "Thu, Feb 19 14:30" (24h)
	static compactDateTimeWithWeekDay(date: DateTime): string {
		return isAMPM() ? date.toFormat('ccc, MMM d h:mma') : date.toFormat('ccc, MMM d HH:mm');
	}

	// e.g. "February 16, 2026"
	static fullDate(date: DateTime): string {
		return date.getLuxonDate().toLocaleString(LuxonDateTime.DATE_FULL);
	}

	// e.g. "Monday, February 16, 2026"
	static fullDateWithWeekDay(date: DateTime): string {
		return date.getLuxonDate().toLocaleString(LuxonDateTime.DATE_HUGE);
	}

	// e.g. "03:45 PM" (12h) or "15:45" (24h)
	static time(date: DateTime): string {
		return isAMPM() ? date.toFormat('hh:mm a') : date.toFormat('HH:mm');
	}

	// e.g. "in 3 hours" / "2 days ago" (useHours=true) or "tomorrow" / "yesterday" (useHours=false, within 24h)
	static relative(date: DateTime, useHours = true): string | null {
		if (useHours || !date.isWithin24Hours()) {
			return date.getLuxonDate().toRelative();
		} else {
			return date.getLuxonDate().toRelativeCalendar();
		}
	}

	// e.g. "03:45 PM" or "15:45" depending on the account's configured hours format
	static asSettingsHoursFormat(date: DateTime): string {
		return date.toFormat(window.global.central.hoursFormat());
	}
}
